export type Sentiment = 'positive' | 'negative' | 'neutral'

export interface SentimentScore {
  word: string
  sentiment: Sentiment
  score: number
}

// Sentiment lexicon - simplified version
// Positive score: 1 to 5, Negative score: -5 to -1
const SENTIMENT_LEXICON: Record<string, number> = {
  // Very positive (4-5)
  amazing: 5,
  awesome: 5,
  excellent: 5,
  fantastic: 5,
  perfect: 5,
  wonderful: 5,
  brilliant: 5,
  outstanding: 5,
  exceptional: 5,
  superb: 5,
  best: 4,
  great: 4,
  love: 4,
  beautiful: 4,
  incredible: 4,
  powerful: 4,
  successful: 4,
  impressive: 4,
  innovative: 4,
  revolutionary: 4,

  // Positive (2-3)
  good: 3,
  better: 3,
  nice: 3,
  helpful: 3,
  easy: 3,
  simple: 3,
  effective: 3,
  efficient: 3,
  useful: 3,
  valuable: 3,
  important: 3,
  interesting: 3,
  strong: 3,
  fast: 3,
  quick: 3,
  clean: 3,
  clear: 3,
  smart: 3,
  advantage: 2,
  benefit: 2,
  improve: 2,
  gain: 2,
  growth: 2,
  increase: 2,
  enhance: 2,
  optimize: 2,
  solution: 2,
  opportunity: 2,

  // Very negative (-5 to -4)
  terrible: -5,
  awful: -5,
  horrible: -5,
  worst: -5,
  disaster: -5,
  failure: -5,
  broken: -4,
  bad: -4,
  poor: -4,
  difficult: -4,
  hard: -4,
  complex: -4,
  complicated: -4,
  problem: -4,
  issue: -4,
  error: -4,
  bug: -4,
  fail: -4,

  // Negative (-3 to -2)
  slow: -3,
  weak: -3,
  limited: -3,
  lacking: -3,
  missing: -3,
  confusing: -3,
  unclear: -3,
  challenging: -2,
  concern: -2,
  risk: -2,
  loss: -2,
  decrease: -2,
  warning: -2,
  deprecated: -2,
  legacy: -2,

  // Technical/Domain positive
  scalable: 3,
  performant: 3,
  reliable: 3,
  secure: 3,
  robust: 3,
  flexible: 3,
  maintainable: 3,
  testable: 3,
  reusable: 3,
  modular: 3,
  modern: 3,
  advanced: 3,
  productivity: 3,
  performance: 3,
  quality: 3,
  success: 4,
  achieve: 3,
  accomplish: 3,
  complete: 2,
  finish: 2,
  deliver: 2,
  ship: 2,
  launch: 3,
  release: 2,
  upgrade: 2,
  update: 2,
  feature: 2,
  capability: 2,
  functionality: 2,
}

/**
 * Analyze sentiment of a word
 * @param word - The word to analyze
 * @returns Sentiment score object
 */
export function analyzeSentiment(word: string): SentimentScore {
  const normalizedWord = word.toLowerCase().trim()
  const score = SENTIMENT_LEXICON[normalizedWord] || 0

  let sentiment: Sentiment = 'neutral'
  if (score > 0) {
    sentiment = 'positive'
  } else if (score < 0) {
    sentiment = 'negative'
  }

  return {
    word,
    sentiment,
    score,
  }
}

/**
 * Get sentiment color for a word
 * @param sentiment - The sentiment type
 * @returns CSS color string
 */
export function getSentimentColor(sentiment: Sentiment): string {
  switch (sentiment) {
    case 'positive':
      return '#10b981' // green
    case 'negative':
      return '#ef4444' // red
    case 'neutral':
      return '#6b7280' // gray
  }
}

/**
 * Analyze sentiment for multiple words with their frequencies
 * @param words - Array of words with frequencies
 * @returns Array of words with sentiment analysis
 */
export function analyzeSentiments(
  words: Array<{ text: string; value: number }>
): Array<{ text: string; value: number; sentiment: Sentiment; score: number }> {
  return words.map(word => {
    const analysis = analyzeSentiment(word.text)
    return {
      ...word,
      sentiment: analysis.sentiment,
      score: analysis.score,
    }
  })
}

/**
 * Calculate sentiment statistics
 * @param words - Array of words with sentiment
 * @returns Sentiment statistics
 */
export function calculateSentimentStats(
  words: Array<{ sentiment: Sentiment; value: number }>
): {
  positive: number
  negative: number
  neutral: number
  totalPositive: number
  totalNegative: number
  totalNeutral: number
  overallScore: number
} {
  let positive = 0
  let negative = 0
  let neutral = 0
  let totalPositive = 0
  let totalNegative = 0
  let totalNeutral = 0

  words.forEach(word => {
    switch (word.sentiment) {
      case 'positive':
        positive++
        totalPositive += word.value
        break
      case 'negative':
        negative++
        totalNegative += word.value
        break
      case 'neutral':
        neutral++
        totalNeutral += word.value
        break
    }
  })

  const total = positive + negative + neutral
  const overallScore = total > 0 ? ((positive - negative) / total) * 100 : 0

  return {
    positive,
    negative,
    neutral,
    totalPositive,
    totalNegative,
    totalNeutral,
    overallScore,
  }
}
